import {
  HttpClient,
  HttpContext,
  HttpErrorResponse,
  HttpHeaders,
  HttpParams,
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@env/insitestore360';
import { SKIP_DUPLICATE_CHECK } from '@insite-store360/auth';
import {
  BLOB_URL,
  Status,
  User,
  USER_URL,
  UserApiResponse,
} from '@insite-store360/shared';
import { BehaviorSubject, catchError, map, Observable, throwError } from 'rxjs';
import { ErrorHandlerService } from './error-handler.service';

@Injectable({
  providedIn: 'root',
})
export class UserServiceService {
  private selectedRowDataSource = new BehaviorSubject<any>(null);
  selectedRowData$ = this.selectedRowDataSource.asObservable();
  private readonly USER_API = `${USER_URL}/user`;
  private readonly AUTH_API = `${USER_URL}`;
  private readonly BLOB_API = `${BLOB_URL}`;
  constructor(
    private http: HttpClient,
    private errorHandler: ErrorHandlerService
  ) { }
  setSelectedRow(data: any): void {
    this.selectedRowDataSource.next(data);
  }

  clearSelectedRow(): void {
    this.selectedRowDataSource.next(null); // Clear the data
  }

  /**
   * Sends a POST request to the server to add a new user's information.
   * @param userDetails - The user data to be added
   * @returns
   */
  // public addUserInfo(userDetails: User): Observable<User> {
  //   return this.http
  //     .post<User>(`${this.USER_API}/addUser`, userDetails)
  //     .pipe(catchError(this.errorHandler.handleError));
  // }

  // /**
  //  * Fetches a list of user information from the server.
  //  * @returns An Observable that emits an array of User objects.
  //  */
  // public getUserInfo(): Observable<User[]> {
  //   return this.http
  //     .get<User[]>(`${this.USER_API}/getUser`)
  //     .pipe(catchError(this.errorHandler.handleError));
  // private api = 'http://localhost:5000/api';
  // constructor(private http: HttpClient) {}
  private handleError(error: HttpErrorResponse) {
    if (error.status === 0) {
    } else {
    }
    return throwError(() => '');
  }
  //api for add user Info
  addUserInfo(data: any): Observable<any> {
    return this.http.post(`${this.USER_API}/addUser`, data);
  }

  //api for get user Info
  getUserInfo(params: {
    pageIndex: number;
    pageSize: number;
    searchFilter?: string;
  }): Observable<UserApiResponse> {
    let queryParams = new HttpParams()
      .set('pageIndex', params.pageIndex)
      .set('pageSize', params.pageSize);
    if (params.searchFilter) {
      queryParams = queryParams.set('searchFilter', params.searchFilter);
    }
    return this.http
      .get<UserApiResponse>(`${this.USER_API}/getUser`, { params: queryParams })
      .pipe(catchError(this.handleError));
  }

  getUserInfoV2(params: {
    pageIndex: number;
    pageSize: number;
    searchFilter?: string;
    userStatus?: number;
    countryIds?: number[];
    regionIds?: number[];
    userRoleId?: number[];
  }): Observable<UserApiResponse> {
    let queryParams = new HttpParams()
      .set('pageIndex', params.pageIndex)
      .set('pageSize', params.pageSize);
    if (params.searchFilter) {
      queryParams = queryParams.set('searchFilter', params.searchFilter);
    }
    if (params.userStatus !== undefined) {
      queryParams = queryParams.set('userStatus', params.userStatus);
    }
    if (params.countryIds && params.countryIds.length > 0) {
      queryParams = queryParams.set('countryIds', params.countryIds.join(','));
    }
    if (params.regionIds && params.regionIds.length > 0) {
      queryParams = queryParams.set('regionIds', params.regionIds.join(','));
    }
    if (params.userRoleId && params.userRoleId.length > 0) {
      queryParams = queryParams.set('userRoleId', params.userRoleId.join(','));
    }

    return this.http
      .get<UserApiResponse>(`${this.USER_API}/getAllV2`, { params: queryParams })
      .pipe(catchError(this.handleError));
  }

  //getting the country details
  public countryDetails(): Observable<any> {
    return this.http
      .get(`${environment.apiUrl}/open/countries`)
      .pipe(catchError(this.errorHandler.handleError));
  }
  //getting the region details
  public RegionDetails(): Observable<any> {
    return this.http
      .get(`${environment.apiUrl}/admin/region`)
      .pipe(catchError(this.errorHandler.handleError));
  }
  //getting the country name details
  public countryName(): Observable<any> {
    return this.http
      .get(`${environment.apiUrl}/admin/countries`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Updates user information on the server.
   * @param id - The unique identifier of the user to be updated.
   * @param data - The user data to be updated.
   * @returns An Observable that emits the updated User object.
   */
  public updateUserInfo(id: number, data: User): Observable<User> {
    return this.http
      .put<User>(`${this.USER_API}/updateUser/${id}`, data)
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Deletes a user from the server.
   * @param id - The unique identifier of the user to be deleted.
   * @returns An Observable that emits a confirmation message upon successful deletion.
   */
  public deleteUser(id: number): Observable<string> {
    return this.http
      .delete<string>(`${this.USER_API}/deleteUser/${id}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Reporting mapping counts for Promoter / TDS / TDL role-change guard.
   */
  public getReportingMappingStatus(userId: number): Observable<{
    asSubordinate: number;
    asManager: number;
    hasMappings: boolean;
  }> {
    return this.http
      .get<{
        asSubordinate: number;
        asManager: number;
        hasMappings: boolean;
      }>(`${this.USER_API}/reportingMappingStatus/${userId}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Imports a list of users into the server.
   * @param data - An array of User objects to be imported.
   * @returns An Observable that emits an array of imported User objects upon successful import.
   */
  public importUsers(data: User[]): Observable<User[]> {
    return this.http
      .post<User[]>(`${this.USER_API}/importUsers`, data)
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Fetches user data from the server and returns it as a Blob.
   * The response type is set to 'blob' to handle binary data, such as files.
   *
   * @returns An Observable emitting a Blob containing the user data.
   */
  public exportUsers(): Observable<Blob> {
    return this.http
      .get<Blob>(`${this.USER_API}/exportUsers`, {
        responseType: 'blob' as 'json',
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Updates the block status of a user.
   *
   * @param userStatus - The object containing the user's ID and the new block status to be updated.
   * @returns An Observable of the updated User object.
   */

  public blockUser(userStatus: Status): Observable<User> {
    return this.http
      .put<User>(
        `${this.USER_API}/updateBlockStatus/${userStatus.id}`,
        userStatus
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Get all user data based on the roles.
   *
   * @returns An Observable of the User object.
   */
  public getMasterUser(params: {
    pageIndex: any,
    pageSize: any,
    searchFilter?: string,
    countryIds?: number[],
    regionIds?: number[],
    userRoleId?: number[],
    userType?: number[],
    userStatus?: number,
    hasPmAccess?: boolean,
    pmCombinedList?: boolean,
    projectId?: number | null,
  }

  ): Observable<UserApiResponse> {
    let queryParams = new HttpParams()
      .set('pageIndex', params.pageIndex)
      .set('pageSize', params.pageSize);
    if (params.searchFilter) {
      queryParams = queryParams.set('searchFilter', params.searchFilter);
    }

    if (params.countryIds && params.countryIds.length > 0) {
      queryParams = queryParams.set('countryIds', params.countryIds.join(','));
    }

    if (params.regionIds && params.regionIds.length > 0) {
      queryParams = queryParams.set('regionIds', params.regionIds.join(','));
    }

    if (params.userRoleId && params.userRoleId.length > 0) {
      queryParams = queryParams.set('userRoleId', params.userRoleId.join(','));
    }

    if (params.userType && params.userType.length > 0) {
      queryParams = queryParams.set('userType', params.userType.join(','));
    }

    if (params.userStatus !== null && params.userStatus !== undefined) {
      queryParams = queryParams.set('userStatus', params.userStatus);
    }

    if (params.hasPmAccess === true) {
      queryParams = queryParams.set('hasPmAccess', 'true');
    }

    if (params.pmCombinedList === true) {
      queryParams = queryParams.set('pmCombinedList', 'true');
    }

    if (params.projectId != null && !Number.isNaN(Number(params.projectId))) {
      queryParams = queryParams.set('projectId', String(params.projectId));
    }

    return this.http
      .get<UserApiResponse>(
        `${this.USER_API}/getAll`, { params: queryParams }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Upload user profile image.
   *
   * @param id - The unique identifier of the user.
   * @param profileUrl - The URL of the profile image to be uploaded.
   * @returns An Observable of the User object.
   */
  uploadUserProfileImage(id: number, profileUrl: FormData): Observable<any> {
    return this.http
      .post(`${this.BLOB_API}/profile/upload/${id}`, profileUrl)
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
 * Upload image.
 *
 * @param fileUrl - The URL of the file image to be uploaded.
 * @returns An Observable of the image url.
 */
  fileUpload(fileUrl: FormData, containerName: string): Observable<any> {
    return this.http
      .post(`${this.BLOB_API}/uploadBlob?containerName=${containerName}`, fileUrl)
      .pipe(catchError(this.errorHandler.handleError));
  }


  // http://localhost:3006/api/profile/upload/${id}
  /**
   * Get user details by id.
   * This method allows multiple simultaneous calls (duplicate requests are not cancelled).
   *
   * @param id - The unique identifier of the user.
   * @returns An Observable of the User object.
   */
  getUserDetailsById(id: number): Observable<any> {
    return this.http
      .get(`${this.USER_API}/getById/${id}`, {
        context: new HttpContext().set(SKIP_DUPLICATE_CHECK, true)
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  updateTestUser(id: number, deleteSurveyRecords?: boolean): Observable<any> {
    return this.http
      .post(`${this.USER_API}/updateTestUser/${id}`, { deleteSurveyRecords })
      .pipe(catchError(this.errorHandler.handleError));
  }

  updateProjectManagementAccess(
    userId: number,
    hasProjectManagementAccess: boolean,
    projectManagementRoleId?: number,
  ): Observable<{ success: boolean }> {
    const body: {
      hasProjectManagementAccess: boolean;
      projectManagementRoleId?: number;
    } = { hasProjectManagementAccess };

    if (hasProjectManagementAccess && projectManagementRoleId != null) {
      body.projectManagementRoleId = projectManagementRoleId;
    }

    return this.http
      .put<{ success: boolean }>(
        `${this.USER_API}/updateProjectManagementAccess/${userId}`,
        body,
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Post to get the tds value based on the country.
   *
   * @body passing role id and country id.
   * @returns An Observable of the Tds object.
   */
  getTdsByCountryId(tdsData: any): Observable<any> {
    return this.http
      .post(`${this.USER_API}/getByRoleAndCountryIds`, tdsData)
      .pipe(catchError(this.errorHandler.handleError));
  }

  // getuserTypesbyRoles(prmoter,TDS,TDL)
  getUserTypeByRoles(userData: any): Observable<any> {
    return this.http
      .get(`${this.USER_API}/getRoleForSurveyMapping`, userData)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getAllUsersForJourneyPlan(
    pageIndex: Number,
    pageSize: Number,
    searchFilter?: string
  ): Observable<any> {
    const id = 138;
    if (searchFilter) {
      return this.http
        .get<any>(
          `${this.USER_API}/getAllUsersForJourneyPlan?id=${id}&pageIndex=${pageIndex}&pageSize=${pageSize}&searchFilter=${searchFilter}`
        )
        .pipe(catchError(this.errorHandler.handleError));
    } else {
      return this.http
        .get<any>(
          `${this.USER_API}/getAllUsersForJourneyPlan?id=${id}&pageIndex=${pageIndex}&pageSize=${pageSize}`
        )
        .pipe(catchError(this.errorHandler.handleError));
    }
  }




  /**
   * Get all user data based on the roles.
   *
   * @returns An Observable of the User object.
   */
  public getAllTDSPromotersDetails(params: {
    pageIndex: any,
    pageSize: any,
    searchFilter?: string,
    country?: number,
    tdsName?: string
  }

  ): Observable<UserApiResponse> {
    let queryParams = new HttpParams()
      .set('pageIndex', params.pageIndex)
      .set('pageSize', params.pageSize);
    if (params.searchFilter) {
      queryParams = queryParams.set('searchFilter', params.searchFilter);
    }

    if (params.country) {
      queryParams = queryParams.set('country', params.country);
    }
    if (params.tdsName) {
      queryParams = queryParams.set('tdsName', params.tdsName);
    }
    return this.http
      .get<UserApiResponse>(
        `${this.USER_API}/getAllTdsView`, { params: queryParams }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }


  /**
 * Fetches user data from the server and returns it as a Blob.
 * The response type is set to 'blob' to handle binary data, such as files.
 *
 * @returns An Observable emitting a Blob containing the user data.
 */
  public exportTDSPromoters(searchValue: string, country: number, tdsName: string): Observable<Blob> {
    const columns: string[] = ['TDS', 'Promoters', 'Store', 'City', 'Area', 'Country'];
    return this.http
      .post<Blob>(`${this.USER_API}/downloadExcelReport`,
        { columns, searchValue, country, tdsName },
        {
          responseType: 'blob' as 'json',
        }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }



  public getAllTdsPromoterSummary(params: {
    pageIndex: any,
    pageSize: any,
    searchFilter?: string,
    country?: number,
    tdsName?: string
  }

  ): Observable<UserApiResponse> {
    let queryParams = new HttpParams()
      .set('pageIndex', params.pageIndex)
      .set('pageSize', params.pageSize);
    if (params.searchFilter) {
      queryParams = queryParams.set('searchFilter', params.searchFilter);
    }

    if (params.country) {
      queryParams = queryParams.set('country', params.country);
    }
    if (params.tdsName) {
      queryParams = queryParams.set('tdsName', params.tdsName);
    }

    return this.http
      .get<UserApiResponse>(
        `${this.USER_API}/getAllTdsPromoterSummary`, { params: queryParams }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }


  public getAllTdsStoreSummary(params: {
    pageIndex: any,
    pageSize: any,
    searchFilter?: string,
    country?: number,
    tdsName?: string
  }

  ): Observable<UserApiResponse> {
    let queryParams = new HttpParams()
      .set('pageIndex', params.pageIndex)
      .set('pageSize', params.pageSize);
    if (params.searchFilter) {
      queryParams = queryParams.set('searchFilter', params.searchFilter);
    }

    if (params.country) {
      queryParams = queryParams.set('country', params.country);
    }
    if (params.tdsName) {
      queryParams = queryParams.set('tdsName', params.tdsName);
    }

    return this.http
      .get<UserApiResponse>(
        `${this.USER_API}/getAllTdsStoreSummary`, { params: queryParams }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }




  public getAllTDSList(tdsName?: string): Observable<UserApiResponse> {
    let queryParams = new HttpParams();
    if (tdsName) {
      queryParams = queryParams.set('tdsName', tdsName);
    }
    return this.http
      .get<UserApiResponse>(
        `${this.USER_API}/getTdsList`, { params: queryParams }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Get all user types based on country ID with count
   * @param countryId - The ID of the country to get user types for
   * @returns An Observable of the user types with count
   */
  public getUserTypesByCountryId(countryId: number[]): Observable<any> {
    return this.http
      .get<any>(`${environment.apiUrl}/user-service/role/getAllBasedOnCountryId?countryId=${countryId}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Get TDS, TDL and Promoter people mapped to a store.
   * GET /user/getStorePeopleByStoreId?storeId=12487
   */
  public getStorePeopleByStoreId(storeId: number): Observable<{
    storeId: number;
    storeName: string | null;
    tds: Array<{
      userId: number;
      fullName: string;
      loginId: string | null;
      email: string | null;
      phoneNumber: string | null;
    }>;
    tdl: Array<{
      userId: number;
      fullName: string;
      loginId: string | null;
      email: string | null;
      phoneNumber: string | null;
    }>;
    promoters: Array<{
      userId: number;
      fullName: string;
      loginId: string | null;
      email: string | null;
      phoneNumber: string | null;
      promoterType: string | null;
    }>;
  }> {
    const queryParams = new HttpParams().set('storeId', String(storeId));
    const skipDup = new HttpContext().set(SKIP_DUPLICATE_CHECK, true);
    return this.http
      .get<any>(`${this.USER_API}/getStorePeopleByStoreId`, {
        params: queryParams,
        context: skipDup,
      })
      .pipe(catchError((err) => this.errorHandler.handleError(err)));
  }

  public getAllUsersMappedStores(
    pageIndex: number,
    pageSize: number,
    searchValue: string,
    countryId?: number,
    userId?: number,
    accountIds?: number[],
    roleId?: number,
    promoterTypeId?: number
  ): Observable<any> {
    let queryParams = new HttpParams()
      .set('pageIndex', pageIndex)
      .set('pageSize', pageSize);
    if (searchValue) {
      queryParams = queryParams.set('searchValue', searchValue);
    }
    if (countryId) {
      queryParams = queryParams.set('countryIds', countryId);
    }
    if (userId) {
      queryParams = queryParams.set('userId', userId);
    }
    if (accountIds && accountIds.length > 0) {
      queryParams = queryParams.set('accountIds', accountIds.join(','));
    }
    if (roleId) {
      queryParams = queryParams.set('roleId', roleId);
    }
    if (promoterTypeId) {
      queryParams = queryParams.set('promoterTypeId', promoterTypeId);
    }
    return this.http
      .get<any>(`${this.USER_API}/getAllUsersMappedStores`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }


  public exportUserStoreMapping(countryIds: number, searchValue: string, userId: number, accountIds?: number[], roleId?: number, promoterTypeId?: number): Observable<Blob> {
    let queryParams = new HttpParams();
    if (countryIds) {
      queryParams = queryParams.set('countryIds', countryIds);
    }
    if (userId) {
      queryParams = queryParams.set('userId', userId);
    }
    if (searchValue) {
      queryParams = queryParams.set('searchValue', searchValue);
    }
    if (accountIds && accountIds.length > 0) {
      queryParams = queryParams.set('accountIds', accountIds.join(','));
    }
    if (roleId) {
      queryParams = queryParams.set('roleId', roleId);
    }
    if (promoterTypeId) {
      queryParams = queryParams.set('promoterTypeId', promoterTypeId);
    }
    return this.http
      .post<Blob>(`${this.USER_API}/exportUserStoreMapping/downloadExcelReport`, {},
        {
          params: queryParams,
          responseType: 'blob' as 'json',
        }
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getPromoterType(): Observable<any> {
    return this.http.get<any>(`${this.USER_API}/getPromoterType`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public syncSms(): Observable<any> {
    return this.http
      .get<any>(`${this.USER_API}/syncsms`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  // --- User Area Group Mapping ---
  public saveUserAreaGroupMapping(mappingDto: any): Observable<any> {
    return this.http
      .post<any>(`${this.USER_API}/userAreaGroupMapping`, mappingDto)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getUserAreaGroupMapping(userId: number): Observable<any> {
    return this.http
      .get<any>(`${this.USER_API}/userAreaGroupMapping/getByUserId/${userId}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getAllUserAreaGroupMappings(): Observable<any> {
    return this.http
      .get<any>(`${this.USER_API}/userAreaGroupMapping/getAll`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getIncentiveSchemeTypes(): Observable<any> {
    return this.http.get<any>(`${this.USER_API}/getIncentiveSchemeTypes`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  /**
   * Updates the sign up active status.
   * @param isActive - boolean indicating if sign up should be active
   * @returns Observable<any>
   */
  public updateSignUpActive(isActive: boolean): Observable<any> {
    return this.http.put(
      `${this.AUTH_API}/auth/update-sign-up-active`,
      { isActive: isActive.toString() }
    );
  }

  public getRegionByCountryId(countryId: number): Observable<any> {
    return this.http.get<any>(`${this.AUTH_API}/region/getByCountryId/${countryId}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getAllAdminRolesByCountryId(countryId: number): Observable<any> {
    let queryParams = new HttpParams();
    if (countryId) {
      queryParams = queryParams.set('countryId', countryId);
    }
    return this.http.get<any>(`${this.AUTH_API}/general-settings/getAllAdminRolesByCountryId`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public sendSelloutDetailsEmail(countryId: number): Observable<{ success: boolean; message: string }> {
    return this.http
      .post<{ success: boolean; message: string }>(
        `${this.AUTH_API}/general-settings/sendSelloutDetailsEmail`,
        { countryId },
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getSelloutAdminUsers(countryId: number): Observable<{ users: { id: number; name: string; email: string }[] }> {
    return this.http
      .get<{ users: { id: number; name: string; email: string }[] }>(
        `${this.AUTH_API}/general-settings/sellout-admin-users/${countryId}`,
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getSelloutAdminUserCandidates(
    countryId: number,
    roleIds: number[],
  ): Observable<{ id: number; name: string }[]> {
    return this.http
      .post<{ id: number; name: string }[]>(
        `${this.AUTH_API}/general-settings/sellout-admin-users/candidates`,
        { countryId, roleIds },
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public saveSelloutAdminUsers(
    countryId: number,
    userIds: number[],
  ): Observable<{ success: boolean; message: string }> {
    return this.http
      .post<{ success: boolean; message: string }>(
        `${this.AUTH_API}/general-settings/sellout-admin-users/save`,
        { countryId, userIds },
      )
      .pipe(catchError(this.errorHandler.handleError));
  }


  public deleteSelloutAdminRecipient(id: number, countryId: number
  ): Observable<{ success: boolean; message: string }> {

    let queryParams = new HttpParams();
    if (id) {
      queryParams = queryParams.set('userId', id);
    }
    if (countryId) {
      queryParams = queryParams.set('countryId', countryId);
    }
    return this.http
      .delete<{ success: boolean; message: string }>(
        `${this.AUTH_API}/general-settings/sellout-admin-users/delete`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public sendSelloutEmailTest(): Observable<{
    success: boolean;
    message: string;
    recipientCount: number;
  }> {
    return this.http
      .post<{
        success: boolean;
        message: string;
        recipientCount: number;
      }>(`${this.AUTH_API}/general-settings/test-sellout-email`, {})
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getSnagReportIncharge(countryId: number): Observable<{ users: { id: number; name: string; email: string }[] }> {
    return this.http
      .get<{ users: { id: number; name: string; email: string }[] }>(
        `${this.AUTH_API}/general-settings/snag-report-incharge/${countryId}`,
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public saveSnagReportIncharge(
    countryId: number,
    userIds: number[],
    correlationId?: string,
  ): Observable<{ success: boolean; message: string }> {
    return this.http
      .post<{ success: boolean; message: string }>(
        `${this.AUTH_API}/general-settings/snag-report-incharge/save`,
        { countryId, userIds, ...(correlationId ? { correlationId } : {}) },
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getActivityPic(countryId: number): Observable<{ users: { id: number; name: string; email: string }[] }> {
    return this.http
      .get<{ users: { id: number; name: string; email: string }[] }>(
        `${this.AUTH_API}/general-settings/activity-pic/${countryId}`,
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public saveActivityPic(
    countryId: number,
    userIds: number[],
    correlationId?: string,
  ): Observable<{ success: boolean; message: string }> {
    return this.http
      .post<{ success: boolean; message: string }>(
        `${this.AUTH_API}/general-settings/activity-pic/save`,
        { countryId, userIds, ...(correlationId ? { correlationId } : {}) },
      )
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getAllPromoterCategories(params: {
    pageIndex: number;
    pageSize: number;
    searchValue?: string;
    countryId?: number;
  }): Observable<any> {
    let queryParams = new HttpParams()
      .set('pageIndex', params.pageIndex)
      .set('pageSize', params.pageSize);

    if (params.searchValue) {
      queryParams = queryParams.set('searchValue', params.searchValue);
    }
    if (params.countryId) {
      queryParams = queryParams.set('countryId', params.countryId);
    }

    return this.http
      .get<any>(`${this.AUTH_API}/mdaSdaCountry/getAll`, { params: queryParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public savePromoterCategory(data: { name: string; countryIds: number[] }): Observable<any> {
    return this.http
      .post<any>(`${this.AUTH_API}/mdaSdaCountry/save`, data)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public updatePromoterCategory(data: { id: number; name: string; countryIds: number[] }): Observable<any> {
    return this.http
      .post<any>(`${this.AUTH_API}/mdaSdaCountry/update`, data)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getPromoterCategoryUsage(id: number): Observable<any> {
    return this.http
      .get<any>(`${this.AUTH_API}/mdaSdaCountry/usage/${id}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public deletePromoterCategory(id: number): Observable<any> {
    return this.http
      .delete<any>(`${this.AUTH_API}/mdaSdaCountry/delete/${id}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  /** Fetch daily target split percentages for a given country/year/month. */
  public getDailyTargetSplits(
    countryId: number,
    year: number,
    month: number,
  ): Observable<{ day: number; percentage: number }[]> {
    const params = new HttpParams()
      .set('countryId', countryId)
      .set('year', year)
      .set('month', month);
    return this.http
      .get<any>(`${this.AUTH_API}/daily-target-split`, { params })
      .pipe(
        map((res: any) => (Array.isArray(res) ? res : res?.result ?? [])),
        catchError(this.errorHandler.handleError),
      );
  }

  /** Paginated list from daily_target_split table with filters + search. */
  public getDailyTargetSplitList(params: {
    countryIds: number[];
    fromDate?: string;
    toDate?: string;
    year?: number;
    month?: number;
    fromDay?: number;
    toDay?: number;
    pageIndex?: number;
    pageSize?: number;
    searchFilter?: string;
  }): Observable<any> {
    let httpParams = new HttpParams().set(
      'countryIds',
      (params.countryIds || []).join(','),
    );
    if (params.fromDate) httpParams = httpParams.set('fromDate', params.fromDate);
    if (params.toDate) httpParams = httpParams.set('toDate', params.toDate);
    if (params.year != null) httpParams = httpParams.set('year', params.year);
    if (params.month != null) httpParams = httpParams.set('month', params.month);
    if (params.fromDay != null) httpParams = httpParams.set('fromDay', params.fromDay);
    if (params.toDay != null) httpParams = httpParams.set('toDay', params.toDay);
    if (params.pageIndex != null) httpParams = httpParams.set('pageIndex', params.pageIndex);
    if (params.pageSize != null) httpParams = httpParams.set('pageSize', params.pageSize);
    if (params.searchFilter) httpParams = httpParams.set('searchFilter', params.searchFilter);

    return this.http
      .get<any>(`${this.AUTH_API}/daily-target-split/list`, { params: httpParams })
      .pipe(catchError(this.errorHandler.handleError));
  }

  /** Export daily_target_split rows (same filters/search as list). */
  public exportDailyTargetSplit(body: {
    countryIds: number[];
    fromDate?: string;
    toDate?: string;
    year?: number;
    month?: number;
    fromDay?: number;
    toDay?: number;
    searchFilter?: string;
  }): Observable<Blob> {
    return this.http
      .post(`${this.AUTH_API}/daily-target-split/downloadExcelReport`, body, {
        responseType: 'blob',
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  /** Download daily target split Excel template for selected year/month(s). */
  public downloadDailyTargetSplitTemplate(
    year: number,
    months: number | number[],
    countryId?: number,
  ): Observable<Blob> {
    const monthList = (Array.isArray(months) ? months : [months])
      .map((month) => Number(month))
      .filter((month) => month > 0);

    let params = new HttpParams().set('year', year);
    if (monthList.length === 1) {
      params = params.set('month', monthList[0]);
    } else if (monthList.length > 1) {
      params = params.set('months', monthList.join(','));
    }
    if (countryId) {
      params = params.set('countryId', countryId);
    }
    return this.http
      .get(`${this.AUTH_API}/daily-target-split/downloadExcelTemplate`, {
        params,
        responseType: 'blob',
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  /** Save daily target split percentages for a given country/year/month. */
  public saveDailyTargetSplits(body: {
    countryId: number;
    year: number;
    month: number;
    splits: { day: number; percentage: number }[];
  }): Observable<{ day: number; percentage: number }[]> {
    return this.http
      .post<any>(`${this.AUTH_API}/daily-target-split/save`, body)
      .pipe(
        map((res: any) => (Array.isArray(res) ? res : res?.result ?? [])),
        catchError(this.errorHandler.handleError),
      );
  }

  /** Import daily target split Excel rows for a country. */
  public importDailyTargetSplitData(body: {
    countryId: number;
    data: { year: number; month: number; day: number; percentage: number }[];
  }): Observable<{
    message: string;
    year: number;
    month: number;
    months?: number[];
    count: number;
  }> {
    return this.http
      .post<any>(`${this.AUTH_API}/daily-target-split/importData`, body)
      .pipe(catchError(this.errorHandler.handleError));
  }

  /** Create a single daily_target_split row. */
  public createDailyTargetSplit(body: {
    countryId: number;
    year: number;
    month: number;
    day: number;
    percentage: number;
  }): Observable<any> {
    return this.http
      .post<any>(`${this.AUTH_API}/daily-target-split/create`, body)
      .pipe(catchError(this.errorHandler.handleError));
  }

  /** Update a single daily_target_split row. */
  public updateDailyTargetSplit(body: {
    id: number;
    countryId: number;
    year: number;
    month: number;
    day: number;
    percentage: number;
  }): Observable<any> {
    return this.http
      .put<any>(`${this.AUTH_API}/daily-target-split/update`, body)
      .pipe(catchError(this.errorHandler.handleError));
  }

  /** Delete a single daily_target_split row by id. */
  public deleteDailyTargetSplit(id: number): Observable<any> {
    return this.http
      .delete<any>(`${this.AUTH_API}/daily-target-split/delete/${id}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getUsersForAreaGroupMapping(params: any): Observable<any> {
    let queryParams = new HttpParams();
    if (params.countryIds && params.countryIds.length > 0) {
      queryParams = queryParams.set('countryIds', params.countryIds.join(','));
    }
    if (params.regionIds && params.regionIds.length > 0) {
      queryParams = queryParams.set('regionIds', params.regionIds.join(','));
    }
    if (params.userRoleId && params.userRoleId.length > 0) {
      queryParams = queryParams.set('userRoleId', params.userRoleId.join(','));
    }
    return this.http
      .get<any>(`${this.USER_API}/userAreaGroupMapping/users`, { params: queryParams })
      .pipe(catchError(this.handleError));
  }

  public getUsersForChannelMapping(params: any): Observable<any> {
    let queryParams = new HttpParams();
    if (params.countryIds && params.countryIds.length > 0) {
      queryParams = queryParams.set('countryIds', params.countryIds.join(','));
    }
    if (params.regionIds && params.regionIds.length > 0) {
      queryParams = queryParams.set('regionIds', params.regionIds.join(','));
    }
    if (params.userRoleId && params.userRoleId.length > 0) {
      queryParams = queryParams.set('userRoleId', params.userRoleId.join(','));
    }
    return this.http
      .get<any>(`${this.USER_API}/userChannelMapping/users`, { params: queryParams })
      .pipe(catchError(this.handleError));
  }

  public saveUserChannelMapping(data: any): Observable<any> {
    return this.http
      .post<any>(`${this.USER_API}/userChannelMapping`, data)
      .pipe(catchError(this.handleError));
  }

  public getUserChannelMapping(userId: number): Observable<any> {
    return this.http
      .get<any>(`${this.USER_API}/userChannelMapping/getByUserId/${userId}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getAllUserChannelMappings(): Observable<any> {
    return this.http
      .get<any>(`${this.USER_API}/userChannelMapping/getAll`)
      .pipe(catchError(this.handleError));
  }

  public getAllBoardPapers(): Observable<any[]> {
    return this.http
      .get<any>(`${this.AUTH_API}/board-paper/getAll`)
      .pipe(
        map((res: any) => (Array.isArray(res) ? res : res?.result ?? [])),
        catchError(this.errorHandler.handleError),
      );
  }

  public getBoardPaperByPeriod(year: number, month: number): Observable<any | null> {
    return this.http
      .get<any>(`${this.AUTH_API}/board-paper/getByPeriod`, {
        params: { year: String(year), month: String(month) },
      })
      .pipe(
        map((res: any) => {
          if (res && typeof res === 'object' && 'result' in res) {
            return res.result ?? null;
          }
          return res ?? null;
        }),
        catchError(this.errorHandler.handleError),
      );
  }

  public uploadBoardPaper(year: number, month: number, file: File): Observable<any> {
    const formData = new FormData();
    formData.append('year', String(year));
    formData.append('month', String(month));
    formData.append('file', file);
    return this.http
      .post<any>(`${this.AUTH_API}/board-paper/upload`, formData)
      .pipe(
        map((res: any) => res?.result ?? res),
        catchError(this.errorHandler.handleError),
      );
  }

  public deleteBoardPaper(id: number): Observable<void> {
    return this.http
      .delete<any>(`${this.AUTH_API}/board-paper/delete/${id}`)
      .pipe(catchError(this.errorHandler.handleError));
  }

  public streamBoardPaperView(year: number, month: number): Observable<Blob> {
    const headers = new HttpHeaders().set('isBlob', 'true');
    return this.http
      .get(`${this.AUTH_API}/board-paper/view`, {
        headers,
        params: { year: String(year), month: String(month) },
        responseType: 'blob',
      })
      .pipe(catchError(this.errorHandler.handleError));
  }

  public getAllCompetitiveIntelligence(): Observable<any[]> {
    return this.http
      .get<any>(`${this.AUTH_API}/competitive-intelligence/getAll`)
      .pipe(
        map((res: any) => (Array.isArray(res) ? res : res?.result ?? [])),
        catchError(this.errorHandler.handleError),
      );
  }

  public getCompetitiveIntelligenceByPeriod(
    year: number,
    month: number,
  ): Observable<any | null> {
    return this.http
      .get<any>(`${this.AUTH_API}/competitive-intelligence/getByPeriod`, {
        params: { year: String(year), month: String(month) },
      })
      .pipe(
        map((res: any) => {
          if (res && typeof res === 'object' && 'result' in res) {
            return res.result ?? null;
          }
          return res ?? null;
        }),
        catchError(this.errorHandler.handleError),
      );
  }

  public uploadCompetitiveIntelligence(
    year: number,
    month: number,
    file: File,
  ): Observable<any> {
    const formData = new FormData();
    formData.append('year', String(year));
    formData.append('month', String(month));
    formData.append('file', file);
    return this.http
      .post<any>(`${this.AUTH_API}/competitive-intelligence/upload`, formData)
      .pipe(
        map((res: any) => res?.result ?? res),
        catchError(this.errorHandler.handleError),
      );
  }

  public deleteCompetitiveIntelligence(id: number): Observable<void> {
    return this.http
      .delete<any>(`${this.AUTH_API}/competitive-intelligence/delete/${id}`)
      .pipe(catchError(this.errorHandler.handleError));
  }
}
